"use client";

/**
 * DocsMock — animated mock of the askFinz product-documentation site for
 * /apps/docs.
 *
 * docs.askfinz.ai is the product documentation site (guides, API reference,
 * recipes, changelog), NOT a live document editor. Layout:
 *   - Top: search box (pre-typed with "rate limits") and pulsing results
 *     dropdown
 *   - Left rail: nested doc tree with active page highlighted
 *   - Center: a documentation page that swaps every ~5s between an overview
 *     page, an API reference page, and a recipe page
 *   - Right rail: on-this-page TOC with active section indicator
 *
 * Pure presentation: no auth, no fetch, no runtime deps. Lives in
 * askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Search,
  Book,
  FileText,
  Info,
  Lightbulb,
  Hash,
  Terminal as TerminalIcon,
  ArrowUpRight,
  Folder,
  FolderOpen,
} from "lucide-react";

type DocNode =
  | { kind: "section"; name: string; open?: boolean; children: DocNode[] }
  | { kind: "page"; name: string; id: string };

// Single source of truth for the doc tree. `active` is computed against the
// current page key at render time.
const TREE: DocNode[] = [
  {
    kind: "section",
    name: "Getting Started",
    open: true,
    children: [
      { kind: "page", name: "Overview",         id: "overview" },
      { kind: "page", name: "Quickstart",       id: "quickstart" },
      { kind: "page", name: "Authentication",   id: "auth" },
    ],
  },
  {
    kind: "section",
    name: "Workspaces",
    open: true,
    children: [
      { kind: "page", name: "Chat",     id: "ws-chat" },
      { kind: "page", name: "Code",     id: "ws-code" },
      { kind: "page", name: "Research", id: "ws-research" },
      { kind: "page", name: "News",     id: "ws-news" },
      { kind: "page", name: "Search",   id: "ws-search" },
      { kind: "page", name: "Dash",     id: "ws-dash" },
      { kind: "page", name: "Storage",  id: "ws-storage" },
      { kind: "page", name: "Workflow", id: "ws-workflow" },
      { kind: "page", name: "Train",    id: "ws-train" },
    ],
  },
  {
    kind: "section",
    name: "API Reference",
    open: true,
    children: [
      { kind: "page", name: "REST",     id: "api-rest" },
      { kind: "page", name: "Webhooks", id: "api-webhooks" },
      { kind: "page", name: "SDKs",     id: "api-sdks" },
    ],
  },
  {
    kind: "section",
    name: "Concepts",
    open: false,
    children: [
      { kind: "page", name: "Agents",       id: "c-agents" },
      { kind: "page", name: "Rate limits",  id: "c-rate-limits" },
    ],
  },
  {
    kind: "section",
    name: "Recipes",
    open: true,
    children: [
      { kind: "page", name: "Stream a chat",         id: "r-stream-chat" },
      { kind: "page", name: "Index a corpus",        id: "r-index-corpus" },
      { kind: "page", name: "Schedule a workflow",   id: "r-schedule" },
    ],
  },
  { kind: "section", name: "Changelog", open: false, children: [{ kind: "page", name: "2026", id: "cl-2026" }] },
  { kind: "section", name: "Glossary",  open: false, children: [{ kind: "page", name: "A–Z",  id: "g-az" }] },
];

type PageKind = "overview" | "api" | "recipe";

type PageDef = {
  kind: PageKind;
  treeId: string;
  breadcrumb: string;
  title: string;
  toc: { id: string; label: string; activeIndex?: boolean }[];
};

const PAGES: PageDef[] = [
  {
    kind: "overview",
    treeId: "overview",
    breadcrumb: "Getting Started · Overview",
    title: "Overview",
    toc: [
      { id: "what",      label: "What is askFinz" },
      { id: "shape",     label: "Shape of the API" },
      { id: "first",     label: "Your first call" },
      { id: "limits",    label: "Rate limits", activeIndex: true },
    ],
  },
  {
    kind: "api",
    treeId: "api-rest",
    breadcrumb: "API Reference · REST · POST /v1/chat",
    title: "POST /v1/chat",
    toc: [
      { id: "params",   label: "Parameters", activeIndex: true },
      { id: "request",  label: "Request" },
      { id: "response", label: "Response schema" },
      { id: "errors",   label: "Errors" },
    ],
  },
  {
    kind: "recipe",
    treeId: "r-stream-chat",
    breadcrumb: "Recipes · Stream a chat",
    title: "Stream a chat completion",
    toc: [
      { id: "goal",     label: "Goal", activeIndex: true },
      { id: "setup",    label: "Setup" },
      { id: "stream",   label: "Stream the response" },
      { id: "next",     label: "What to try next" },
    ],
  },
];

const SEARCH_HITS = [
  { page: "Concepts · Rate limits",          snippet: "Per-account RPS, burst tokens and the 429 backoff window…", selected: true },
  { page: "API Reference · REST",            snippet: "Headers `x-ratelimit-remaining` and `x-ratelimit-reset`…" },
  { page: "Recipes · Schedule a workflow",   snippet: "Spreading bursts across the minute to avoid rate-limit walls…" },
  { page: "Changelog · 2026",                snippet: "2026-03-11 — rate-limit headers now returned on every response." },
  { page: "Glossary",                        snippet: "Rate limit — a per-account ceiling expressed as RPS plus a burst…" },
];

export function DocsMock() {
  const [pageIdx, setPageIdx] = useState(0);
  const [searchPulse, setSearchPulse] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  // Drive the script when in view, and loop it forever.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const runLoop = () => {
      // Each cycle: dwell on a page, pulse the search box, swap to next page.
      const CYCLE_MS = 5200;
      PAGES.forEach((_, i) => {
        schedule(() => {
          setPageIdx(i);
          // When landing on the API page, expand the response schema mid-dwell.
          if (PAGES[i].kind === "api") {
            setSchemaOpen(false);
            schedule(() => setSchemaOpen(true), 2200);
          }
          // Pulse the search dropdown about a second after the swap.
          schedule(() => setSearchPulse(true), 600);
          schedule(() => setSearchPulse(false), 1800);
        }, i * CYCLE_MS);
      });
      // Restart the loop after the last cycle finishes.
      schedule(runLoop, PAGES.length * CYCLE_MS);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(runLoop, 400);
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  const page = PAGES[pageIdx];

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            docs.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <Book className="h-3 w-3" />
            Docs
          </span>
        </div>

        {/* Search bar */}
        <div className="border-b hairline px-3 py-2 bg-foreground/[0.02] relative">
          <div className="flex items-center gap-2 rounded-md border hairline-strong bg-background px-3 py-1.5 text-[12px] max-w-md">
            <Search className="h-3.5 w-3.5 text-foreground/65" />
            <span className="text-foreground/85">rate limits</span>
            <span className="inline-block h-3 w-0.5 bg-foreground/55 animate-pulse ml-0.5" />
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-foreground/65 font-mono">
              <kbd className="rounded border hairline px-1 py-0.5 text-[9px]">⌘K</kbd>
            </span>
          </div>

          {/* Results dropdown */}
          <div
            className={
              "absolute left-3 top-[44px] z-20 w-full max-w-md rounded-lg border hairline-strong bg-card shadow-xl overflow-hidden transition-all " +
              (searchPulse ? "opacity-100 translate-y-0" : "opacity-90 translate-y-0")
            }
            style={{ boxShadow: searchPulse ? "0 0 0 2px hsl(var(--accent) / 0.18)" : undefined }}
          >
            <div className="px-3 py-1.5 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
              {SEARCH_HITS.length} results
            </div>
            <div>
              {SEARCH_HITS.map((h, i) => (
                <div
                  key={i}
                  className={
                    "px-3 py-2 border-b hairline last:border-b-0 transition " +
                    (h.selected ? "bg-foreground/[0.05]" : "hover:bg-foreground/[0.02]")
                  }
                >
                  <div className="flex items-center gap-1.5 text-[11px] text-foreground/80">
                    <FileText className="h-3 w-3 text-foreground/65 shrink-0" />
                    <span className="truncate">{h.page}</span>
                    {h.selected && (
                      <span className="ml-auto text-[9px] font-mono text-foreground/65">↵</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-foreground/65 truncate pl-[18px] leading-snug">
                    {h.snippet}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[460px] md:min-h-[520px]">
          {/* Left rail: doc tree */}
          <aside className="col-span-12 md:col-span-3 border-r hairline bg-foreground/[0.02] p-2 text-[12px] max-h-[520px] overflow-hidden">
            <div className="px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              Documentation
            </div>
            <Tree nodes={TREE} indent={0} activeId={page.treeId} />
          </aside>

          {/* Center: page content */}
          <main className="col-span-12 md:col-span-7 border-r hairline px-6 py-6 bg-background overflow-hidden">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
              {page.breadcrumb}
            </div>
            <h2 className="mt-2 display-text text-[24px] tracking-tight">{page.title}</h2>

            {/* Body */}
            <div key={`${pageIdx}`} className="mt-5 animate-[blur-fade-in_0.45s_ease-out]">
              {page.kind === "overview" && <OverviewBody />}
              {page.kind === "api" && <ApiBody schemaOpen={schemaOpen} />}
              {page.kind === "recipe" && <RecipeBody />}
            </div>
          </main>

          {/* Right rail: on-this-page */}
          <aside className="hidden md:flex md:col-span-2 flex-col bg-foreground/[0.015] p-3">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              On this page
            </div>
            <ul className="space-y-1 text-[11px]">
              {page.toc.map((t) => (
                <li
                  key={t.id}
                  className={
                    "flex items-center gap-1.5 leading-snug " +
                    (t.activeIndex ? "text-foreground" : "text-foreground/65 hover:text-foreground/85")
                  }
                >
                  {t.activeIndex && (
                    <span className="inline-block h-1 w-1 rounded-full bg-foreground" />
                  )}
                  <span className={t.activeIndex ? "" : "pl-2.5"}>{t.label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t hairline text-[10px] font-mono text-foreground/65 leading-snug">
              Edit this page on{" "}
              <span className="text-foreground/65">github</span>
            </div>
          </aside>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Live docs at <span className="font-mono text-foreground/65">docs.askfinz.ai</span></span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page bodies                                                         */
/* ------------------------------------------------------------------ */

function OverviewBody() {
  return (
    <div className="space-y-4 text-[13px] leading-relaxed text-foreground/85">
      <p>
        askFinz exposes a single multi-LLM agent surface across nine product
        workspaces. The REST API is provider-agnostic — pick a model, send a
        prompt, stream the response. Authentication is per-account; rate
        limits are documented per route.
      </p>

      <div className="rounded-md border hairline bg-foreground/[0.03] p-3 flex gap-2.5">
        <Info className="h-4 w-4 text-foreground/65 shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-foreground/75">
          <span className="font-medium text-foreground/90">Info · </span>
          Every workspace shares one auth token and one quota — there is no
          per-product key to manage.
        </div>
      </div>

      <p className="text-[12.5px] tracking-[0.14em] uppercase text-foreground/65 font-mono pt-1">
        Your first call
      </p>

      <div className="rounded-lg border hairline bg-foreground/[0.04] font-mono text-[11.5px] overflow-hidden">
        <div className="px-3 py-1.5 border-b hairline flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
          <TerminalIcon className="h-3 w-3" />
          curl
        </div>
        <pre className="px-3 py-2 text-foreground/85 leading-[1.55] whitespace-pre overflow-x-auto">
{`curl https://api.askfinz.ai/v1/chat \\
  -H "Authorization: Bearer $ASKFINZ_TOKEN" \\
  -d '{"model":"frontier-fast","prompt":"Hello"}'`}
        </pre>
      </div>

      <div className="rounded-md border hairline bg-foreground/[0.025] p-2.5 flex gap-2.5">
        <Lightbulb className="h-4 w-4 text-foreground/65 shrink-0 mt-0.5" />
        <div className="text-[12px] text-foreground/70">
          <span className="font-medium text-foreground/85">Tip · </span>
          Set <code className="font-mono text-[11.5px] bg-foreground/[0.06] px-1 rounded">stream: true</code>{" "}
          to receive server-sent events instead of a single JSON response.
        </div>
      </div>
    </div>
  );
}

function ApiBody({ schemaOpen }: { schemaOpen: boolean }) {
  return (
    <div className="space-y-4 text-[13px] leading-relaxed text-foreground/85">
      <p className="text-foreground/80">
        Send a chat completion. Streams tokens as server-sent events when{" "}
        <code className="font-mono text-[11.5px] bg-foreground/[0.06] px-1 rounded">stream</code> is true.
      </p>

      <div className="text-[11px] tracking-[0.14em] uppercase text-foreground/65 font-mono">Parameters</div>

      <div className="overflow-hidden rounded-md border hairline">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b hairline bg-foreground/[0.04] text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <th className="text-left px-3 py-1.5 font-mono">Field</th>
              <th className="text-left px-3 py-1.5 font-mono">Type</th>
              <th className="text-left px-3 py-1.5 font-mono">Required</th>
            </tr>
          </thead>
          <tbody className="font-mono text-[11.5px] text-foreground/80">
            {[
              { f: "model",       t: "string",  r: "yes" },
              { f: "prompt",      t: "string",  r: "yes" },
              { f: "stream",      t: "boolean", r: "no" },
              { f: "max_tokens",  t: "integer", r: "no" },
              { f: "temperature", t: "number",  r: "no" },
            ].map((row, i) => (
              <tr key={row.f} className={i < 4 ? "border-b hairline" : ""}>
                <td className="px-3 py-1.5">{row.f}</td>
                <td className="px-3 py-1.5 text-foreground/65">{row.t}</td>
                <td className="px-3 py-1.5 text-foreground/65">{row.r}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-[11px] font-mono text-foreground/70 hover:text-foreground transition"
        aria-hidden
      >
        {schemaOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Response schema
      </button>

      {schemaOpen && (
        <div className="rounded-md border hairline bg-foreground/[0.04] font-mono text-[11.5px] overflow-hidden animate-[blur-fade-in_0.4s_ease-out]">
          <div className="px-3 py-1.5 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            200 · application/json
          </div>
          <pre className="px-3 py-2 text-foreground/85 leading-[1.55] whitespace-pre overflow-x-auto">
{`{
  "id": "chat_2c4f...",
  "model": "frontier-fast",
  "output": "Hello there. How can I help?",
  "usage": { "prompt_tokens": 4, "output_tokens": 9 },
  "finish_reason": "stop"
}`}
          </pre>
        </div>
      )}
    </div>
  );
}

function RecipeBody() {
  return (
    <div className="space-y-4 text-[13px] leading-relaxed text-foreground/85">
      <p>
        Walk through streaming a chat completion end-to-end. You&#39;ll
        authenticate, send a prompt, and render each token as it arrives.
      </p>

      <div className="rounded-md border hairline bg-foreground/[0.025] p-2.5 flex gap-2.5">
        <Lightbulb className="h-4 w-4 text-foreground/65 shrink-0 mt-0.5" />
        <div className="text-[12px] text-foreground/70">
          <span className="font-medium text-foreground/85">Tip · </span>
          Streaming pairs well with{" "}
          <code className="font-mono text-[11.5px] bg-foreground/[0.06] px-1 rounded">EventSource</code> in browsers
          or any SSE client server-side.
        </div>
      </div>

      <div className="text-[11px] tracking-[0.14em] uppercase text-foreground/65 font-mono">Stream the response</div>

      <div className="rounded-lg border hairline bg-foreground/[0.04] font-mono text-[11.5px] overflow-hidden">
        <div className="px-3 py-1.5 border-b hairline flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
          <Hash className="h-3 w-3" />
          typescript
        </div>
        <pre className="px-3 py-2 text-foreground/85 leading-[1.55] whitespace-pre overflow-x-auto">
{`import { askFinz } from "@askfinz/sdk";

const af = new askFinz({ token: process.env.ASKFINZ_TOKEN });

for await (const chunk of af.chat.stream({
  model:  "frontier-fast",
  prompt: "Summarise this article in three bullets.",
})) {
  process.stdout.write(chunk.delta);
}`}
        </pre>
      </div>

      <div className="rounded-md border hairline bg-foreground/[0.03] p-3 flex gap-2.5">
        <Info className="h-4 w-4 text-foreground/65 shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-foreground/75">
          <span className="font-medium text-foreground/90">Info · </span>
          Streams terminate with a{" "}
          <code className="font-mono text-[11.5px] bg-foreground/[0.06] px-1 rounded">[DONE]</code>{" "}
          sentinel; the SDK swallows that for you.
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tree                                                                */
/* ------------------------------------------------------------------ */

function Tree({ nodes, indent, activeId }: { nodes: DocNode[]; indent: number; activeId: string }) {
  return (
    <div>
      {nodes.map((n, i) => {
        const pad = { paddingLeft: 6 + indent * 12 };
        if (n.kind === "section") {
          return (
            <div key={`s-${i}`}>
              <div
                className="flex items-center gap-1.5 py-0.5 rounded text-foreground/75 text-[11.5px]"
                style={pad}
              >
                {n.open ? (
                  <FolderOpen className="h-3 w-3 text-foreground/65" />
                ) : (
                  <Folder className="h-3 w-3 text-foreground/65" />
                )}
                <span className="font-medium">{n.name}</span>
              </div>
              {n.open && <Tree nodes={n.children} indent={indent + 1} activeId={activeId} />}
            </div>
          );
        }
        const active = n.id === activeId;
        return (
          <div
            key={`p-${i}`}
            className={
              "flex items-center gap-1.5 py-0.5 rounded text-[11.5px] transition " +
              (active
                ? "bg-foreground text-background"
                : "text-foreground/65 hover:bg-foreground/5")
            }
            style={pad}
          >
            <FileText className="h-3 w-3 opacity-65" />
            <span>{n.name}</span>
          </div>
        );
      })}
    </div>
  );
}
